import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShoppingBag, Heart, User, Search, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR } from "@/lib/api";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

const nav = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/collection/men", label: "Men" },
  { to: "/collection/women", label: "Women" },
  { to: "/collection/best-sellers", label: "Best Sellers" },
  { to: "/collection/new-arrivals", label: "New Arrivals" },
  { to: "/collections", label: "Collections" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!searchOpen) return;
    const q = searchQ.trim();
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      const url = q ? `/products?q=${encodeURIComponent(q)}` : "/products";
      api.get(url, { signal: ctrl.signal }).then((r) => setResults(r.data.slice(0, 6))).catch(() => {});
    }, 180);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [searchQ, searchOpen]);

  const submitSearch = (e) => {
    e?.preventDefault();
    setSearchOpen(false);
    navigate(`/shop${searchQ.trim() ? `?q=${encodeURIComponent(searchQ.trim())}` : ""}`);
  };

  return (
    <>
      <AnnouncementBar />
      <header className="sticky top-0 z-50 bg-black text-white border-b border-white/5" data-testid="site-header">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          {/* Row 1: icons + logo + icons */}
          <div className="h-[68px] flex items-center justify-between">
            {/* Mobile menu */}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-btn" aria-label="Menu" className="p-2 -ml-2">
                    <Menu size={22} />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-black text-white border-white/10 w-[85vw] p-0 overflow-y-auto">
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <SheetDescription className="sr-only">Site navigation</SheetDescription>
                  <nav className="mt-16 flex flex-col px-8 gap-1 pb-10">
                    {nav.map((l) => (
                      <SheetClose asChild key={l.to}>
                        <Link to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                          className="font-display text-3xl py-2 tracking-tight text-white/90 hover:text-gold transition-colors">
                          {l.label}
                        </Link>
                      </SheetClose>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop left placeholder for balance */}
            <div className="hidden lg:block w-40"></div>

            {/* Brand center */}
            <Link to="/" data-testid="brand-logo"
              className="font-display text-3xl md:text-4xl tracking-[0.42em] uppercase text-gold">
              VYRON
            </Link>

            {/* Right icons */}
            <div className="flex items-center gap-5 md:gap-6">
              <button onClick={() => setSearchOpen(true)} data-testid="search-toggle" aria-label="Search" className="hover:text-gold transition-colors">
                <Search size={20} strokeWidth={1.5} />
              </button>
              <Link to={user ? "/account" : "/login"} data-testid="account-link" aria-label="Account" className="hover:text-gold transition-colors">
                <User size={20} strokeWidth={1.5} />
              </Link>
              <Link to="/wishlist" data-testid="wishlist-link" aria-label="Wishlist" className="relative hover:text-gold transition-colors">
                <Heart size={20} strokeWidth={1.5} />
                {wishCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-gold text-black text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full">{wishCount}</span>
                )}
              </Link>
              <button onClick={() => setOpen(true)} data-testid="cart-toggle" aria-label="Cart" className="relative hover:text-gold transition-colors">
                <ShoppingBag size={20} strokeWidth={1.5} />
                <span data-testid="cart-count" className="absolute -top-1.5 -right-1.5 bg-gold text-black text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full">{count}</span>
              </button>
            </div>
          </div>

          {/* Row 2: nav (desktop) */}
          <nav className="hidden lg:flex items-center justify-center gap-8 xl:gap-10 h-[46px] border-t border-white/5">
            {nav.map((l) => {
              const active = location.pathname === l.to || (l.to !== "/" && location.pathname.startsWith(l.to));
              return (
                <NavLink key={l.to} to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  className={`relative text-[11px] uppercase tracking-[0.22em] whitespace-nowrap transition-colors ${active ? "text-gold" : "text-white/80 hover:text-white"}`}>
                  {l.label}
                  {active && <span className="absolute -bottom-[13px] left-0 right-0 h-[2px] bg-gold" />}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center pt-24" data-testid="search-overlay" onClick={() => setSearchOpen(false)}>
          <div className="w-[92%] max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={submitSearch} className="flex items-center border-b border-black/10">
              <Search size={20} className="ml-6 opacity-60" />
              <input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                data-testid="search-input"
                placeholder="Search VYRON frames…"
                className="flex-1 px-5 py-5 outline-none text-lg bg-transparent" />
              <button type="button" onClick={() => setSearchOpen(false)} className="p-5 opacity-60 hover:opacity-100"><X size={20} /></button>
            </form>
            <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results">
              {results.length === 0 && (
                <p className="p-6 text-sm text-muted-foreground text-center">Try “gold”, “tortoise”, “polarized”…</p>
              )}
              {results.map((p) => (
                <Link key={p.slug} to={`/product/${p.slug}`} onClick={() => setSearchOpen(false)}
                  data-testid={`search-result-${p.slug}`}
                  className="flex items-center gap-4 px-6 py-3 hover:bg-[#F5F5F5] border-b border-black/5">
                  <img src={p.images?.[0]} alt="" className="w-14 h-14 rounded-xl object-contain bg-[#F5F5F5]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-[0.14em]">{p.tagline}</p>
                  </div>
                  <span className="text-sm shrink-0">{formatINR(p.price)}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
